import { getToken } from './auth';

export const DIAGNOSTICS_BUILD = 'phone-20261021-1';
const number = (v, cap = 86400) => Number.isFinite(v) ? Math.round(Math.max(0, Math.min(cap, v)) * 1000) / 1000 : 0;

// Observers only: never call play/pause/load, alter src/time, or retry a failed POST.
// Caller explicitly opts in for this mounted player session. No local persistence.
export function startDiagnostics(audio, getState) {
  if (!audio) return () => {};
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const session = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    const started = performance.now();
    let queue = [], stopped = false, lastSend = -Infinity;
    let lastTime = audio.currentTime, lastAdvance = started, episode = false, lastKey = '';
    const seen = new Map(), requests = new Set(), listeners = [];
    const safe = fn => (...args) => { try { if (!stopped) fn(...args); } catch (_) { /* diagnostics must not escape */ } };
    const flush = safe(() => {
      const now = performance.now();
      if (!queue.length || now - lastSend < 10000) return;
      lastSend = now;
      const events = queue.splice(0, 10); // drop attempted batch even on failure
      const token = getToken();
      if (!token) return;
      const controller = new AbortController();
      const timer = setTimeout(() => { controller.abort(); requests.delete(controller); }, 5000);
      requests.add(controller);
      try {
        Promise.resolve(fetch('/api/v1/player-diagnostics', {
          method: 'POST', credentials: 'same-origin', mode: 'same-origin', redirect: 'error',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({build: DIAGNOSTICS_BUILD, session, events}),
          signal: controller.signal, keepalive: true, referrerPolicy: 'no-referrer',
        })).catch(() => {}).finally(() => { clearTimeout(timer); requests.delete(controller); });
      } catch (_) { clearTimeout(timer); requests.delete(controller); }
    });
    const record = safe(event => {
      const now = performance.now();
      if (now - (seen.get(event) ?? -Infinity) < 1000) return;
      seen.set(event, now);
      const state = getState();
      const buffered = [];
      let ahead = 0;
      for (let i = 0; i < Math.min(audio.buffered.length, 8); i++) {
        const start = number(audio.buffered.start(i)), end = number(audio.buffered.end(i));
        buffered.push([start, end]);
        if (start <= audio.currentTime && audio.currentTime <= end) ahead = end - audio.currentTime;
      }
      queue.push({event, utc: Date.now(), mono: number(now - started, 86400000),
        song: Number.isInteger(state.song) && state.song > 0 && state.song <= 2147483647 ? state.song : 0,
        track: state.track === 2 ? 2 : 1, intent: !!state.intent,
        paused: !!audio.paused, ended: !!audio.ended, seeking: !!audio.seeking,
        current: number(audio.currentTime), duration: number(audio.duration),
        ready: audio.readyState, network: audio.networkState, buffered, ahead: number(ahead),
        visible: document.visibilityState === 'visible', online: navigator.onLine !== false,
        error: [1, 2, 3, 4].includes(audio.error?.code) ? audio.error.code : 0});
      if (queue.length > 50) queue.shift();
    });
    const listen = (target, name, fn) => { const wrapped = safe(fn); target.addEventListener(name, wrapped); listeners.push(() => target.removeEventListener(name, wrapped)); };
    ['waiting','stalled','pause','error','playing','loadstart','ended','seeking','seeked'].forEach(event => {
      listen(audio, event, () => {
        record(event);
        if (['loadstart', 'seeking', 'seeked', 'ended'].includes(event)) {
          lastTime = audio.currentTime; lastAdvance = performance.now(); episode = false;
        }
        if (event === 'error') flush();
      });
    });
    listen(document, 'visibilitychange', () => {
      record('visibility'); lastAdvance = performance.now(); lastTime = audio.currentTime; episode = false;
      flush();
    });
    for (const event of ['online','offline']) listen(window, event, () => record(event));
    listen(window, 'pagehide', () => { record('pagehide'); flush(); });
    const timer = setInterval(safe(() => {
      const now = performance.now(), state = getState(), key = `${state.song}:${state.track}`;
      const active = state.intent && !audio.ended && !audio.seeking && document.visibilityState === 'visible';
      if (!active || key !== lastKey) {
        lastAdvance = now; lastTime = audio.currentTime; episode = false;
      } else if (audio.currentTime > lastTime + 0.1) {
        if (episode) record('recovery');
        episode = false; lastAdvance = now; lastTime = audio.currentTime;
      } else if (!episode && now - lastAdvance >= 10000) {
        episode = true; record('no_progress');
      }
      lastKey = key;
      flush();
    }), 1000);
    const stop = () => {
      stopped = true; clearInterval(timer); clearTimeout(expiry);
      listeners.forEach(remove => remove()); requests.forEach(controller => controller.abort());
      queue = [];
    };
    const expiry = setTimeout(stop, 3600000); // hard one-hour client lifetime
    record('start'); flush();
    return stop;
  } catch (_) { return () => {}; }
}
